'use client';

import Link from 'next/link';
// Note: SEO meta tags should be handled by generateMetadata in page.tsx (App Router pattern)
// next/head is NOT compatible with Next.js App Router
import type { BreadcrumbItem } from './Breadcrumbs';
import FAQSection, { type FAQItem } from './FAQSection';
import { RelatedGlossaryTerms } from './RelatedGlossaryTerms';
import StickyMobileCTA from './StickyMobileCTA';
import AdSenseLoader from './AdSenseLoader';
import AdUnit from './AdUnit';
import dynamic from 'next/dynamic';
const FloatingHelpButton = dynamic(() => import('./FloatingHelpButton'), { ssr: false });
const ExitIntentPopup = dynamic(() => import('./ExitIntentPopup'), { ssr: false });
import IntentSteeringBanner from './IntentSteeringBanner';
import ScrollReveal from './ScrollReveal';
import PrivacyNotice from './PrivacyNotice';
import ToolFeedbackWidget from './ToolFeedbackWidget';
import AffiliateRecommendation from './AffiliateRecommendation';
import { useHeadingIds } from '@/hooks/useHeadingIds';
import { useToolAnalytics } from '@/hooks/useToolAnalytics';
import { buildContactUrl } from '@/lib/utils/contact-url';
import { isConsumerTool } from '@/lib/data/consumer-pages';
import { SourceBreadcrumb } from '@/components/tools';
import { getToolPrivacyInfo } from '@/lib/data/tool-privacy';
import { type ReactNode, useState, useEffect } from 'react';

/**
 * CTA Section Configuration
 */
interface ToolCTA {
  title: string;
  description: string;
  primaryButton?: {
    text: string;
    href: string;
    className?: string;
  };
  secondaryButton?: {
    text: string;
    href: string;
    className?: string;
  };
}

/**
 * Related Tool
 */
interface RelatedTool {
  name: string;
  slug: string;
  description: string;
  category: string;
  icon?: string;
}

/**
 * Citation
 */
interface Citation {
  title: string;
  author?: string;
  organization?: string;
  year?: string;
  url?: string;
  accessed?: string;
}

/**
 * External Resource - Links to partner/related external tools
 */
interface ExternalResource {
  name: string;
  url: string;
  description: string;
  siteName?: string;  // e.g., "Number Convert", "Financial Toolset"
}

/**
 * Related Blog Post - For intent steering when users want to learn more
 */
interface RelatedBlogPost {
  title: string;
  slug: string;
  excerpt?: string;
}

/**
 * Parent Tool - For nested tool pages (e.g., CVE Lookup subpages)
 */
interface ParentTool {
  name: string; // Display name (e.g., "CVE Lookup")
  slug: string; // URL slug (e.g., "cve-lookup")
}

/**
 * HowTo Step for Schema.org structured data
 * Used to generate HowTo rich snippets in search results
 */
interface HowToStep {
  name: string;        // Step title (e.g., "Enter your input")
  text: string;        // Step description
  image?: string;      // Optional image URL for the step
}

/**
 * ToolLayout Props
 */
interface ToolLayoutProps {
  // Basic Info
  toolName: string;
  toolSlug: string;
  toolDescription?: string;
  category: string; // e.g., "security", "developer", "networking"
  categorySlug?: string; // URL slug for category (e.g., "security")
  categoryName?: string; // Display name for category (e.g., "Security")
  parentTool?: ParentTool; // For nested tool pages (adds breadcrumb: Category > Parent Tool > Current)

  // SEO
  keywords?: string[];
  ogImage?: string;
  metaTitle?: string; // Explicit SEO title override
  metaDescription?: string; // Explicit SEO description override

  // Hero Section
  heroGradient?: string; // Default: 'from-[#2cc3d1] to-[#2813e8]'

  // Educational Content (appears after tool, before CTA)
  educationalContent?: ReactNode;

  // Citations (appears after educational content)
  citations?: Citation[];
  showCitations?: boolean; // Default: true if citations provided

  // CTA Section
  ctaSection?: ToolCTA;
  showCTA?: boolean; // Default: true

  // Pre-resolved ServiceMapping for the exit-intent popup (D1-backed).
  // Passed through to ExitIntentPopup so it doesn't fall back to the static file.
  exitIntentServiceMapping?: import('@/lib/data/content-service-mapping').ServiceMapping;

  // FAQ
  faqItems?: FAQItem[];

  // Glossary Terms
  glossaryTerms?: { slug: string; term: string; shortDefinition: string }[];
  showGlossary?: boolean; // Default: true

  // Related Tools
  relatedTools?: RelatedTool[];
  showRelatedTools?: boolean; // Default: true

  // External Resources (partner tools, related external sites)
  externalResources?: ExternalResource[];
  showExternalResources?: boolean; // Default: true if resources provided

  // Related Blog Post (for intent steering - "Want to learn more?")
  relatedBlogPost?: RelatedBlogPost;
  showIntentSteering?: boolean; // Default: true if relatedBlogPost provided

  // Disclaimer
  showDisclaimer?: boolean; // Default: true
  disclaimerVariant?: 'security' | 'general'; // Default: 'general'

  // Privacy Notice
  showPrivacyNotice?: boolean; // Default: true, auto-detects from registry
  privacyOverride?: 'client-only' | 'external-api' | 'none'; // Override auto-detection

  // HowTo Schema
  howToSteps?: HowToStep[]; // Custom steps for HowTo schema (generic steps generated if not provided)
  primaryActionLabel?: string; // Label for the primary action button (e.g., "Calculate", "Generate", "Convert")
  showHowToSchema?: boolean; // Default: true

  // Schema Control
  skipClientSchema?: boolean; // When true, server component handles all schema (avoids duplication)

  // Shareable result actions (issue #65)
  // Optional, prop-driven slot rendered above the tool — typically a
  // "Copy shareable link" button for tools wired with the url-state deep-linking
  // library. Tools loaded via the R2 bundle render their own button inside
  // ToolClient instead; this slot is for tools with a dedicated page.tsx.
  shareActions?: ReactNode;

  // Main tool component
  children: ReactNode;
}

/**
 * ToolLayout Component
 *
 * Standard layout for all tools on InventiveHQ.com
 * Structure: Title → Description → Tool → CTA → Education → Citations → Related Terms → FAQ → Related Tools → Disclaimer
 */
export default function ToolLayout({
  toolName,
  toolSlug,
  toolDescription,
  category,
  categorySlug,
  categoryName,
  parentTool,
  keywords,
  ogImage,
  metaTitle, // Destructure new prop
  metaDescription, // Destructure new prop
  heroGradient = 'from-[#2cc3d1] to-[#2813e8]',
  educationalContent,
  citations,
  showCitations = true,
  ctaSection,
  exitIntentServiceMapping,
  showCTA = true,
  faqItems,
  glossaryTerms,
  showGlossary = true,
  relatedTools,
  showRelatedTools = true,
  externalResources,
  showExternalResources = true,
  relatedBlogPost,
  showIntentSteering = true,
  showDisclaimer = true,
  disclaimerVariant = 'general',
  showPrivacyNotice = true,
  privacyOverride,
  howToSteps,
  primaryActionLabel,
  showHowToSchema = true,
  skipClientSchema = false,
  shareActions,
  children,
}: ToolLayoutProps) {
  // Hydration guard to prevent React error #418
  // Hooks that have side effects must only run after hydration is complete
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Add IDs to all headings for deep linking (only after hydration)
  useHeadingIds(isHydrated);

  // Track tool usage analytics (only after hydration)
  const { trackInteraction } = useToolAnalytics(toolSlug, isHydrated);

  // SEO metadata
  const siteUrl = 'https://inventivehq.com';
  // Canonical URL uses category-based path if categorySlug is provided
  // URL structure: /tools/[category]/[tool] (cleaner than /tools/category/[category]/[tool])
  const canonicalUrl = categorySlug
    ? `${siteUrl}/tools/${categorySlug}/${toolSlug}`
    : `${siteUrl}/tools/${toolSlug}`;
  const finalPageTitle = metaTitle || `${toolName} - Free Online Tool | Inventive HQ`; // Use metaTitle if provided
  const finalDescription = metaDescription || toolDescription || `Free ${toolName.toLowerCase()} tool. No signup required, completely private, and always free.`; // Use metaDescription if provided
  // The old /og-images/tool-${slug}.png path was never populated by any build
  // step, so it 404'd as a social card. Fall back to the dynamic /api/og route,
  // which renders a real subtitle and per-category visual treatment.
  const finalOgImage =
    ogImage ||
    `${siteUrl}/api/og?title=${encodeURIComponent(toolName)}&type=tools` +
      (category ? `&category=${encodeURIComponent(category.toLowerCase())}` : '') +
      (finalDescription ? `&subtitle=${encodeURIComponent(finalDescription)}` : '');

  // Display name for category in breadcrumbs
  const displayCategoryName = categoryName || category;

  // Breadcrumbs - include category if categorySlug is provided
  // Category page is at /tools/category/[slug] but tool URL is /tools/[category]/[tool]
  // If parentTool is provided, add it as intermediate level: Tools > Category > Parent Tool > Current
  const breadcrumbs: BreadcrumbItem[] = (() => {
    const crumbs: BreadcrumbItem[] = [{ label: 'Tools', href: '/tools' }];

    if (categorySlug) {
      crumbs.push({ label: displayCategoryName, href: `/tools/${categorySlug}` });
    }

    if (parentTool) {
      // Parent tool URL: /tools/[category]/[parent-slug] or /tools/[parent-slug]
      const parentHref = categorySlug
        ? `/tools/${categorySlug}/${parentTool.slug}`
        : `/tools/${parentTool.slug}`;
      crumbs.push({ label: parentTool.name, href: parentHref });
    }

    crumbs.push({ label: toolName });
    return crumbs;
  })();

  // Build personalized contact URL
  const contactUrl = buildContactUrl({
    src: 'tool',
    id: toolSlug,
    cat: categorySlug || category,
  });

  const isConsumerPage = isConsumerTool(toolSlug);

  // Default CTA if not provided - action-oriented copy
  const defaultCTA: ToolCTA = {
    title: category === 'security'
      ? "Don't leave your security to chance"
      : "Ready to take this to the next level?",
    description: category === 'security'
      ? "Get a free security assessment from our experts. We'll identify vulnerabilities and create a protection plan tailored to your business."
      : "Our team can help implement enterprise-grade solutions. Get personalized recommendations in a free 30-minute consultation.",
    primaryButton: {
      text: category === 'security' ? "Get Free Security Assessment" : "Schedule Free Consultation",
      href: contactUrl
    },
    secondaryButton: {
      text: "See How We Help",
      href: "/services"
    }
  };

  const finalCTA = ctaSection || defaultCTA;

  // Map category to schema.org applicationCategory
  const getApplicationCategory = (cat: string): string => {
    const categoryMap: Record<string, string> = {
      'security': 'SecurityApplication',
      'developer': 'DeveloperApplication',
      'networking': 'UtilitiesApplication',
      'planning': 'BusinessApplication',
      'math': 'EducationalApplication',
      'conversion': 'UtilitiesApplication',
      'encoding': 'DeveloperApplication',
      'cryptography': 'SecurityApplication',
      'analysis': 'UtilitiesApplication',
      'compliance': 'BusinessApplication',
    };
    return categoryMap[cat.toLowerCase()] || 'WebApplication';
  };

  // Structured data for tool (SoftwareApplication)
  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": toolName,
    "applicationCategory": getApplicationCategory(category),
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": finalDescription,
    "url": canonicalUrl,
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "provider": {
      "@type": "Organization",
      "name": "Inventive HQ",
      "url": "https://inventivehq.com"
    }
  };

  // Add featureList from FAQ questions if available
  if (faqItems && faqItems.length > 0) {
    structuredData["featureList"] = faqItems.slice(0, 5).map(faq => faq.question);
  }

  // FAQ Schema (if FAQ items are provided)
  const faqSchema = faqItems && faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // BreadcrumbList Schema
  // Note: All breadcrumb items should have the 'item' URL property for valid structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.label,
      // Use the canonical URL for the current page if no href is provided (last breadcrumb item)
      "item": crumb.href ? `${siteUrl}${crumb.href}` : canonicalUrl
    }))
  };

  // HowTo Schema - Generate steps for rich snippets
  // Uses custom steps if provided, otherwise generates generic steps based on tool type
  const generateDefaultHowToSteps = (): HowToStep[] => {
    // Determine the primary action based on tool category and name
    const action = primaryActionLabel || (() => {
      const nameLower = toolName.toLowerCase();
      if (nameLower.includes('calculator')) return 'Calculate';
      if (nameLower.includes('generator')) return 'Generate';
      if (nameLower.includes('converter')) return 'Convert';
      if (nameLower.includes('checker') || nameLower.includes('validator')) return 'Check';
      if (nameLower.includes('analyzer') || nameLower.includes('analysis')) return 'Analyze';
      if (nameLower.includes('decoder')) return 'Decode';
      if (nameLower.includes('encoder')) return 'Encode';
      if (nameLower.includes('lookup') || nameLower.includes('search')) return 'Search';
      if (nameLower.includes('builder')) return 'Build';
      if (nameLower.includes('tester')) return 'Test';
      if (nameLower.includes('formatter')) return 'Format';
      return 'Process';
    })();

    return [
      {
        name: `Navigate to the ${toolName}`,
        text: `Open the ${toolName} in your web browser. The tool is free to use and requires no signup or installation.`
      },
      {
        name: 'Enter your input data',
        text: `Provide the required input in the designated field. ${category === 'Security' ? 'All processing happens locally in your browser for privacy.' : 'The tool will process your data instantly.'}`
      },
      {
        name: `Click ${action}`,
        text: `Click the "${action}" button to process your input. Results will appear immediately below the input area.`
      },
      {
        name: 'Review and use your results',
        text: 'Review the output and copy it to your clipboard or download it as needed. You can adjust inputs and re-process as many times as needed.'
      }
    ];
  };

  const howToSchema = showHowToSchema ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Use the ${toolName}`,
    "description": finalDescription,
    "image": finalOgImage,
    "totalTime": "PT2M", // Estimated 2 minutes for most tools
    "tool": {
      "@type": "HowToTool",
      "name": "Web Browser"
    },
    "step": (howToSteps || generateDefaultHowToSteps()).map((step, index) => {
      const stepData: Record<string, unknown> = {
        "@type": "HowToStep",
        "position": index + 1,
        "name": step.name,
        "text": step.text,
        "url": `${canonicalUrl}#step-${index + 1}`
      };
      if (step.image) {
        stepData["image"] = step.image;
      }
      return stepData;
    })
  } : null;

  return (
    <>
      {/* Note: SEO meta tags are handled by generateMetadata in page.tsx (App Router pattern) */}

      {/* Structured Data - Only render client-side schemas when not server-rendered by ToolLayoutWrapper */}
      {!skipClientSchema && (
        <>
          <script
            id={`ld-json-webapp-${toolSlug}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData)
            }}
          />
          {faqSchema && (
            <script
              id={`ld-json-faq-${toolSlug}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(faqSchema)
              }}
            />
          )}
          <script
            id={`ld-json-breadcrumb-${toolSlug}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbSchema)
            }}
          />
          {howToSchema && (
            <script
              id={`ld-json-howto-${toolSlug}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(howToSchema)
              }}
            />
          )}
        </>
      )}

      <div className="min-h-screen bg-white dark:bg-gray-800" suppressHydrationWarning>
        {/* 1. HERO SECTION - Title & Description */}
        <section className={`bg-gradient-to-br ${heroGradient} text-white py-16 px-4`}>
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <div className="mb-6 text-sm overflow-x-auto whitespace-nowrap">
              <Link href="/" className="text-white/80 hover:text-white transition-colors py-1 inline-block">
                Home
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <span key={index}>
                  <span className="mx-2 text-white/60">/</span>
                  {crumb.href ? (
                    <Link href={crumb.href} className="text-white/80 hover:text-white transition-colors py-1 inline-block">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-white">{crumb.label}</span>
                  )}
                </span>
              ))}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {toolName}
            </h1>
            {toolDescription && (
              <p className="text-xl opacity-90 max-w-3xl">
                {toolDescription}
              </p>
            )}

            {/* Privacy Notice - inline in hero for trust signal visibility */}
            {showPrivacyNotice && privacyOverride !== 'none' && (() => {
              const privacyInfo = getToolPrivacyInfo(toolSlug);
              const variant = privacyOverride || (privacyInfo?.type ?? 'client-only');

              return (
                <div className="mt-6">
                  <PrivacyNotice
                    variant={variant}
                    externalServices={privacyInfo?.externalServices}
                    className="!bg-white/10 dark:bg-gray-800/10 !border-white/20 max-w-xl [&_*]:!text-white/90 [&_.text-green-800 dark:text-green-300]:!text-white [&_.text-green-700 dark:text-green-300]:!text-white/80 [&_.text-green-600 dark:text-green-400]:!text-white [&_.text-amber-800 dark:text-amber-300]:!text-white [&_.text-amber-700 dark:text-amber-300]:!text-white/80 [&_.text-amber-600 dark:text-amber-400]:!text-white"
                  />
                </div>
              );
            })()}
          </div>
        </section>

        {/* 2. TOOL SECTION - Main interactive component */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Source breadcrumb when arriving from another tool */}
            <SourceBreadcrumb />

            {/* Intent Steering Banner - For users who want to learn more about the concept */}
            {showIntentSteering && relatedBlogPost && (
              <IntentSteeringBanner
                variant="tool-to-blog"
                primaryBlogPost={{
                  title: relatedBlogPost.title,
                  slug: relatedBlogPost.slug,
                  excerpt: relatedBlogPost.excerpt,
                }}
              />
            )}

            {/* Shareable result actions slot (issue #65) — e.g. "Copy shareable link" */}
            {shareActions && (
              <div className="mb-4 flex flex-wrap justify-end gap-2">
                {shareActions}
              </div>
            )}

            {/* Tool/result card — wordmark makes screenshots of results self-attribute */}
            <div className="relative">
              {children}
              <div
                aria-hidden="true"
                className="mt-3 flex justify-end text-xs font-medium tracking-wide text-gray-500 dark:text-gray-400 select-none"
              >
                inventivehq.com
              </div>
            </div>
          </div>
        </section>

        {/* Below-result ad — consumer tool pages only. Manual unit because Auto
            Ads underperforms on thin/interactive tool pages. */}
        {isConsumerPage && <AdUnit slot="6421786737" />}

        {/* 2.5 AFFILIATE RECOMMENDATION - context-matched offer (renders only when an
            activated offer matches this tool/category; see lib/data/affiliate-offers.ts) */}
        <AffiliateRecommendation category={categorySlug || category} toolSlug={toolSlug} />

        {/* 3. CTA SECTION - Professional services pitch */}
        {showCTA && !isConsumerPage && (
          <section className="py-16 px-4 bg-gradient-to-br from-[#2cc3d1] to-[#2813e8] text-white min-h-[280px] sm:min-h-[240px]">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {finalCTA.title}
              </h2>
              <p className="text-lg opacity-90 mb-8">
                {finalCTA.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {finalCTA.primaryButton && (
                  <Link
                    href={finalCTA.primaryButton.href}
                    className={
                      finalCTA.primaryButton.className ||
                      "inline-block px-8 py-4 rounded-md font-semibold bg-white dark:bg-gray-800 text-[#2813e8] hover:bg-gray-100 dark:hover:bg-gray-800 transition-all active:scale-95"
                    }
                    onClick={() => trackInteraction({
                      interactionType: 'cta_click',
                      interactionData: { button: 'primary', text: finalCTA.primaryButton?.text }
                    })}
                  >
                    {finalCTA.primaryButton.text}
                  </Link>
                )}
                {finalCTA.secondaryButton && (
                  <Link
                    href={finalCTA.secondaryButton.href}
                    className={
                      finalCTA.secondaryButton.className ||
                      "inline-block px-8 py-4 rounded-md font-semibold bg-transparent border-2 border-white text-white hover:bg-white dark:hover:bg-gray-800 hover:text-[#2813e8] transition-all active:scale-95"
                    }
                    onClick={() => trackInteraction({
                      interactionType: 'cta_click',
                      interactionData: { button: 'secondary', text: finalCTA.secondaryButton?.text }
                    })}
                  >
                    {finalCTA.secondaryButton.text}
                  </Link>
                )}
              </div>
            </div>
          </section>
        )}

        {/* 4. EDUCATIONAL CONTENT - How it works, best practices, etc. */}
        {educationalContent && (
          <section className="py-12 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              {educationalContent}
            </div>
          </section>
        )}

        {/* 5. CITATIONS - Sources and references */}
        {showCitations && citations && citations.length > 0 && (
          <section className="py-12 px-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-8">
                References & Citations
              </h2>
              <div className="prose prose-lg max-w-none">
                <ol className="space-y-4">
                  {citations.map((citation, index) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      <div className="flex flex-col gap-1">
                        <div>
                          {citation.author && <span className="font-semibold">{citation.author}. </span>}
                          {citation.organization && !citation.author && (
                            <span className="font-semibold">{citation.organization}. </span>
                          )}
                          {citation.year && <span>({citation.year}). </span>}
                          <span className="italic">{citation.title}</span>
                          {citation.organization && citation.author && (
                            <span>. {citation.organization}</span>
                          )}
                          {citation.url && (
                            <>
                              . Retrieved from{' '}
                              <a
                                href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#2cc3d1] hover:text-[#2813e8] underline break-all"
                              >
                                {citation.url}
                              </a>
                            </>
                          )}
                          {citation.accessed && (
                            <span className="text-gray-500 dark:text-gray-400 text-sm"> (accessed {citation.accessed})</span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 rounded">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Note:</strong> These citations are provided for informational and educational purposes.
                    Always verify information with the original sources and consult with qualified professionals
                    for specific advice related to your situation.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 6. RELATED GLOSSARY TERMS */}
        {showGlossary && (
          <div className="px-4">
            <div className="max-w-7xl mx-auto">
              <RelatedGlossaryTerms
                toolSlug={toolSlug}
                terms={glossaryTerms}
                maxTerms={4}
              />
            </div>
          </div>
        )}

        {/* 7. FAQ SECTION */}
        {faqItems && faqItems.length > 0 && (
          <div className="px-4">
            <div className="max-w-7xl mx-auto">
              <FAQSection items={faqItems} toolName={toolName} />
            </div>
          </div>
        )}

        {/* 8. RELATED TOOLS */}
        {showRelatedTools && relatedTools && relatedTools.length > 0 && (
          <section className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4">
                  Explore More Tools
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Continue with these related tools
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedTools.map((tool, index) => (
                  <ScrollReveal key={tool.slug} animation="fade-up" delay={index * 100}>
                  <Link
                    href={`/tools/${tool.slug}`}
                    className="block bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-[#2cc3d1] hover:shadow-xl transition-all duration-200 group"
                    onClick={() => trackInteraction({
                      interactionType: 'related_tool_click',
                      interactionData: { targetTool: tool.slug, targetName: tool.name }
                    })}
                  >
                    <div className="mb-4">
                      <span className="inline-block text-xs font-bold text-[#1a6f8f] uppercase tracking-wide bg-blue-50 dark:bg-blue-950/40 px-3 py-1 rounded-full">
                        {tool.category}
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      {tool.icon && (
                        <span className="text-3xl flex-shrink-0">{tool.icon}</span>
                      )}
                      <div>
                        <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-gray-100 group-hover:text-[#2cc3d1] transition-colors">
                          {tool.name}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                          {tool.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 text-[#1a6f8f] text-sm font-semibold flex items-center">
                      Try it now
                      <svg
                        className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 9. EXTERNAL RESOURCES */}
        {showExternalResources && externalResources && externalResources.length > 0 && (
          <section className="py-12 px-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4B4F58] mb-2">
                  Related External Resources
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Additional tools from our partner sites
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {externalResources.map((resource, index) => (
                  <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:border-[#2cc3d1] hover:shadow-md transition-all duration-200 group"
                    onClick={() => trackInteraction({
                      interactionType: 'external_resource_click',
                      interactionData: { url: resource.url, name: resource.name, siteName: resource.siteName }
                    })}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-[#2cc3d1] transition-colors">
                        {resource.name}
                      </h3>
                      <svg
                        className="w-4 h-4 text-gray-400 group-hover:text-[#2cc3d1] flex-shrink-0 ml-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {resource.description}
                    </p>
                    {resource.siteName && (
                      <span className="text-xs text-[#1a6f8f] font-medium">
                        {resource.siteName}
                      </span>
                    )}
                  </a>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 10. DISCLAIMER (if needed) */}
        {showDisclaimer && (
          <section className="py-8 px-4 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border-l-4 border-yellow-500">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {disclaimerVariant === 'security' ? '⚠️ Security Notice' : 'ℹ️ Disclaimer'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {disclaimerVariant === 'security' ? (
                    <>
                      This tool is provided for <strong>educational and authorized security testing purposes only</strong>.
                      Always ensure you have proper authorization before testing any systems or networks you do not own.
                      Unauthorized access or security testing may be illegal in your jurisdiction.
                      All processing happens client-side in your browser - no data is sent to our servers.
                    </>
                  ) : (
                    <>
                      This tool is provided for informational and educational purposes only.
                      All processing happens entirely in your browser - no data is sent to or stored on our servers.
                      While we strive for accuracy, we make no warranties about the completeness or reliability of results.
                      Use at your own discretion.
                    </>
                  )}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* 11. FEEDBACK WIDGET - "Was this tool helpful?" */}
        <ToolFeedbackWidget toolSlug={toolSlug} />

        {/* Conversion CTAs — suppressed on consumer tool pages (we show AdSense there instead) */}
        {isConsumerPage ? (
          <>
            <AdSenseLoader />
            {/* Bottom-of-page ad — manual unit, consumer tool pages only */}
            <AdUnit slot="5108705067" />
          </>
        ) : (
          <>
            {/* 12. STICKY MOBILE CTA - Appears after scrolling on mobile */}
            <StickyMobileCTA
              text={category === 'security' ? 'Get a free security assessment' : 'Talk to an expert'}
              href={contactUrl}
              ctaLocation="tool_page"
              scrollThreshold={300}
            />

            {/* 13. FLOATING HELP BUTTON - Desktop only, expands to mini contact form */}
            <FloatingHelpButton
              pageType="tool_page"
              pageSlug={toolSlug}
              category={category}
            />

            {/* 14. EXIT INTENT POPUP - Captures leaving visitors */}
            <ExitIntentPopup
              pageType="tool_page"
              pageSlug={toolSlug}
              category={category}
              serviceMapping={exitIntentServiceMapping}
            />
          </>
        )}
      </div>
    </>
  );
}

/**
 * Export types for use in tool pages
 */
export type { ToolCTA, RelatedTool, Citation, ExternalResource, HowToStep, ToolLayoutProps };
